<template>
  <!-- Jeśli dostępny jest niestandardowy szablon, renderujemy wygenerowany HTML -->
  <div v-if="customTemplate" v-html="generatedHtml"  class="products__single-product-container q-col-sm-12 q-col-lg-6" ></div>
  <!-- W przeciwnym razie renderujemy domyślną strukturę -->
  <div v-else :key="result.id" class="products__single-product-container q-col-sm-12 q-col-lg-6">
    <div class="products__single-product q-p-1 q-position-relative q-d-flex">
      <div class="products__single-product-image-container q-col-4 q-col-lg-5 q-p-0">
        <img class="products__single-product-image q-p-1 q-img-fluid" :src="staticImage" :alt="result.title" :width="width" :height="height">
      </div>
      <div class="products__single-product-info q-col-8 q-col-lg-7 q-px-2 q-text-black q-text-start">
        <p class="products__single-product-title q-p-0 q-m-0 q-fw-semibold">
          <span v-html="highlightedTitle"></span>
        </p>
        <AutocompletePriceBox :product="firstVariant" />
        <a :href="actualLink" @click="handleClick" class="q-stretched-link"></a>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineProps, inject, ref, toRefs } from 'vue';
import { generateStaticImage, addDisplayMacros, formatPrice, parsePrice, getDiscount, getRealPrice } from '@/core/utils';
import { createTemplate, validateTemplate } from '@/core/Template';
import { SmartSearchConfigInterface } from '@/core/types';
import AutocompletePriceBox from '@/components/AutocompletePopup/AutocompletePriceBox.vue';
import { useSearchQueryStore } from '@/store/SearchQueryStore';
import { storeToRefs } from 'pinia';
import { highlightText } from '@/core/highlight';

// Rozszerzamy props o customTemplate, który będzie zawierał HTML szablonu z endpointu
const props = defineProps(['result', 'customTemplate']);
const { link, link_click, variants, id } = toRefs(props.result);
const firstVariant = ref(variants.value[0]);

const searchQueryStore = useSearchQueryStore();
const { searchQuery } = storeToRefs(searchQueryStore);
const highlightedTitle = computed(() => highlightText(
  props.result.title ?? '',
  searchQuery.value ?? '',
  'products__single-product-title-highlight',
));


// Pobranie konfiguracji
const SmartSearchConfig = ref(inject<SmartSearchConfigInterface>('SmartSearchConfig')!);
const { width, height } = SmartSearchConfig.value.onfocus.productImageSize;
const { useQuarticImages } = SmartSearchConfig.value.serp.productImageSize;
const { currency: currencyCode, removeDecimals } = SmartSearchConfig.value.appConfiguration;

// Obliczanie ścieżki do obrazu
const staticImage = computed(() => {
  const imageLink = firstVariant.value.image_link;
  if (!useQuarticImages) {
    return imageLink;
  }
  return imageLink
      ? generateStaticImage(imageLink, width, height)
      : generateStaticImage('https://static.quartic.com.pl/img/no-image/no-image.png', width, height);
});

// Obsługa linku
const actualLink = ref(link.value);
const handleClick = () => {
  actualLink.value = link_click.value.replace(/^http:/, 'https:');
};

// Dodatkowe funkcje i obliczenia dla makr
const priceData = computed(() => {
  const customMapping = window._QONizer?.customSmartSearchConfig?.customPriceMapping;

  const priceKey = customMapping?.price || 'price';
  const oldPriceKey = customMapping?.oldPrice || 'old_price';
  const salePriceKey = customMapping?.salePrice || 'sale_price';

  const v = firstVariant.value;
  const rawPrice = v[priceKey];
  const rawOldPrice = v[oldPriceKey];
  const rawSalePrice = v[salePriceKey];

  const priceToParse = (typeof rawPrice === 'string' || typeof rawPrice === 'number') ? rawPrice : v.price;
  const oldPriceToParse = (typeof rawOldPrice === 'string' || typeof rawOldPrice === 'number') ? rawOldPrice : v.old_price;
  const salePriceToParse = (typeof rawSalePrice === 'string' || typeof rawSalePrice === 'number') ? rawSalePrice : v.sale_price;

  const secondPrice = (oldPriceToParse || salePriceToParse) as string | number | null;
  const parsedPrice = parsePrice(priceToParse as string | number | null);
  const parsedOldPrice = parsePrice(secondPrice);
  const realPrices = getRealPrice(parsedPrice, parsedOldPrice);

  return {
    price: realPrices.price,
    oldPrice: realPrices.oldPrice,
    parsedPrice,
    parsedOldPrice
  };
});

const omnibusPrice = computed(() => parsePrice(firstVariant.value.omnibus_price));

const discountValue = computed(() => {
  const { price, oldPrice, parsedPrice, parsedOldPrice } = priceData.value;
  if (parsedPrice && parsedOldPrice && parsedPrice >= parsedOldPrice) {
    return null;
  }
  return getDiscount(price, oldPrice);
});

const discount = computed(() => discountValue.value);

const formattedPrice = computed(() => formatPrice(priceData.value.price, currencyCode, removeDecimals));
const formattedPriceOld = computed(() => formatPrice(priceData.value.oldPrice, currencyCode, removeDecimals));
const formattedOmnibusPrice = computed(() => formatPrice(omnibusPrice.value, currencyCode, removeDecimals));

// Funkcja przygotowująca czysty link z modyfikacją protokołu
const prepareClearLink = (link: string, clickLink: string) => {
  return ` href="${link}" data-href="${clickLink.replace(/^http:/, 'https:')}" onmousedown="return this.href = this.getAttribute('data-href');" ontouchstart="return this.href = this.getAttribute('data-href');" `;
};

// Mechanizm generowania HTML przy użyciu customTemplate
const generatedHtml = computed(() => {
  if (!props.customTemplate) return '';

  // Rozszerzamy dane produktu o dodatkowe właściwości (makra)
  const v = { ...firstVariant.value };
  v.discount = discount.value ? `- ${discount.value}%` : null;
  v.image_link_cdn = staticImage.value;
  v.price_formatted = formattedPrice.value;
  v.price_number = priceData.value.price;
  v.old_price_formatted = formattedPriceOld.value;
  v.old_price_number = priceData.value.oldPrice;
  v.omnibus_price_formatted = formattedOmnibusPrice.value;
  v.omnibus_price_number = omnibusPrice.value;
  v.id = id.value;
  v.link_click = link_click.value;
  v.clear_link = prepareClearLink(link.value, link_click.value);

  // Możemy dodatkowo modyfikować obiekt za pomocą funkcji addDisplayMacros
  addDisplayMacros(v);

  return createTemplate(props.customTemplate, v, variants.value);
});
</script>

<style>
.products__single-product-image {
  height: auto;
  width: auto;
  margin: auto;
  min-width: 100px;
  max-width: 100%;
  max-height: 135px;
}
@media (max-width: 991.98px) {
  .products__single-product-image {
    height: auto;
    width: auto;
    margin: auto;
    min-width: 100px;
    max-width: 100%;
    max-height: 135px;
  }
}

.products__single-product-title {
  font-size: 0.75em;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

mark.products__single-product-title-highlight {
  background: #dc35453b!important;
  padding: 0 !important;
}

.products__single-product {
  border-bottom: 1px solid #f0f2f6;
  min-height: 100%;
  transition: background-color 0.3s ease;
}
.products__single-product:hover {
  background-color: #f2f2f2;
}

.q-stretched-link {
  cursor: pointer;
}
</style>
